import { useState } from 'react';
import type { ReactNode } from 'react';
import { formatDate } from '../lib/format.ts';
import {
  describePoint,
  describeRoundChange,
  formatRoundValue,
  missingInLatest,
} from '../lib/rounds.ts';
import type { AuditRound, RoundMetricPoint, RoundMetricSeries, RoundSection } from '../../../shared/types.ts';
import { LineChart, Sparkline } from './Charts.tsx';
import { NewBadge } from './NewBadge.tsx';
import { Modal } from './ui.tsx';

const toneClass = (tone: 'up' | 'down' | 'flat' | undefined): string =>
  tone === 'up' ? 'text-success' : tone === 'down' ? 'text-danger' : 'text-muted';

/**
 * Tabela de uma família de métricas ao longo das rodadas. Sempre três colunas de valor (referência,
 * anterior, atual) mais a variação e um mini-gráfico com todas as rodadas; o histórico completo abre
 * ao clicar na linha. Com 3 ou com 30 rodadas a tabela tem o mesmo tamanho.
 */
export function RoundTable({
  title,
  section,
  series,
  rounds,
  latest,
  footer,
}: {
  title: string;
  section: RoundSection;
  series: RoundMetricSeries[];
  rounds: AuditRound[];
  latest: AuditRound | null;
  footer?: ReactNode;
}) {
  const [openMetric, setOpenMetric] = useState<string | null>(null);
  if (series.length === 0) return null;

  const dates = [...new Set(series.flatMap((s) => s.points.map((p) => p.roundDate)))].sort();
  const current = dates.at(-1) ?? null;
  const previous = dates.length >= 2 ? dates[dates.length - 2]! : null;
  const reference = dates.length >= 3 ? dates[0]! : null;
  if (!current) return null;

  const columns: { key: string; date: string; kind: 'Referência' | 'Anterior' | 'Atual' }[] = [];
  if (reference) columns.push({ key: 'ref', date: reference, kind: 'Referência' });
  if (previous) columns.push({ key: 'prev', date: previous, kind: 'Anterior' });
  columns.push({ key: 'cur', date: current, kind: 'Atual' });

  const missing = missingInLatest(latest, section, series, current);
  const isNewest = latest?.roundDate === current && latest.covers.includes(section);
  const opened = series.find((s) => s.metric === openMetric) ?? null;
  const at = (s: RoundMetricSeries, date: string | null): RoundMetricPoint | null =>
    date ? (s.points.find((p) => p.roundDate === date) ?? null) : null;

  return (
    <section className={`card table-wrap${isNewest ? ' card--new' : ''}`}>
      <div className="card__header">
        <h2 className="text-title">{title}</h2>
        {isNewest && <NewBadge date={current} />}
      </div>
      {missing && (
        <p className="callout round-notice">
          A rodada de {formatDate(missing.latestDate)} não trouxe estes números: {missing.reason} Os valores abaixo são da
          rodada de {formatDate(missing.dataDate)} ({missing.age}).
        </p>
      )}
      <table className="table">
        <thead>
          <tr>
            <th>Métrica</th>
            {columns.map((c) => (
              <th key={c.key} className="align-right">
                {c.kind}
                <div className="text-micro text-muted">{formatDate(c.date)}</div>
              </th>
            ))}
            <th className="align-right">{previous ? 'Variação' : ''}</th>
            <th className="align-right">Evolução</th>
          </tr>
        </thead>
        <tbody>
          {series.map((s) => {
            const cur = at(s, current);
            const prev = at(s, previous);
            const change = describeRoundChange(prev?.value ?? null, cur?.value ?? null, s.unit);
            return (
              <tr
                key={s.metric}
                className="row--clickable"
                tabIndex={0}
                onClick={() => setOpenMetric(s.metric)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpenMetric(s.metric);
                  }
                }}
                aria-label={`${s.label}: abrir histórico das rodadas`}
              >
                <td>{s.label}</td>
                {columns.map((c) => {
                  const p = at(s, c.date);
                  return (
                    <td
                      key={c.key}
                      className={`align-right tabular-nums${c.kind === 'Atual' ? '' : ' text-muted'}`}
                      title={p ? describePoint(p) : 'Sem este número nesta rodada'}
                    >
                      {formatRoundValue(p?.value ?? null, s.unit)}
                      {p && p.observedOn !== p.roundDate && (
                        <div className="text-micro text-muted">em {formatDate(p.observedOn)}</div>
                      )}
                    </td>
                  );
                })}
                <td className={`align-right tabular-nums ${toneClass(change?.tone)}`}>{change?.text ?? '—'}</td>
                <td className="align-right">
                  <Sparkline
                    values={s.points.map((p) => p.value)}
                    label={`${s.label}: ${s.points.length} rodadas, de ${formatRoundValue(s.points[0]!.value, s.unit)} a ${formatRoundValue(s.points.at(-1)!.value, s.unit)}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="text-micro text-muted">
        Variação da rodada atual contra a anterior. Passe o mouse num valor para ver a janela e a fonte; clique na linha
        para o histórico ({dates.length} rodada(s) com este dado, de {rounds.length} importada(s)).
        {footer && <> {footer}</>}
      </div>
      {opened && <HistoryModal series={opened} onClose={() => setOpenMetric(null)} />}
    </section>
  );
}

function HistoryModal({ series, onClose }: { series: RoundMetricSeries; onClose: () => void }) {
  const format = (v: number) => formatRoundValue(v, series.unit);
  return (
    <Modal title={`${series.label}: histórico por rodada`} onClose={onClose} wide>
      <LineChart
        points={series.points.map((p) => ({ date: p.roundDate, value: p.value }))}
        label={`${series.label} ao longo das rodadas`}
        format={format}
        tickFormat={series.unit === 'num' ? undefined : format}
      />
      <table className="table">
        <thead>
          <tr>
            <th>Rodada</th>
            <th className="align-right">Valor</th>
            <th className="align-right">Variação</th>
            <th>Janela e fonte</th>
          </tr>
        </thead>
        <tbody>
          {[...series.points].reverse().map((p, i, all) => {
            const before = all[i + 1] ?? null;
            const change = describeRoundChange(before?.value ?? null, p.value, series.unit);
            return (
              <tr key={p.roundDate}>
                <td>{formatDate(p.roundDate)}</td>
                <td className="align-right tabular-nums">{format(p.value)}</td>
                <td className={`align-right tabular-nums ${toneClass(change?.tone)}`}>
                  {change ? `${change.text} desde ${formatDate(before!.roundDate)}` : '—'}
                </td>
                <td className="text-micro text-muted">{describePoint(p)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Modal>
  );
}
